import { useState, useRef, useEffect } from 'react';
import { ImagePlus, Trash2, Upload, Link2, Check, X } from 'lucide-react';

const ACCENT = '#0066cc';

function SmallBtn({ onClick, bg = ACCENT, children }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding:'2px 10px', border:'none', borderRadius:'4px',
        background: h ? darken(bg) : bg,
        color:'#fff', fontSize:'0.75rem', fontWeight:600,
        cursor:'pointer', fontFamily:'inherit', transition:'background 0.15s',
      }}>{children}</button>
  );
}

function darken(hex) {
  try {
    const n = parseInt(hex.replace('#',''),16);
    return `rgb(${Math.max(0,(n>>16)-30)},${Math.max(0,((n>>8)&0xff)-30)},${Math.max(0,(n&0xff)-30)})`;
  } catch { return hex; }
}

/* ── EditableText ── */
export function EditableText({ value, onSave, tag:Tag='p', style, isAdmin, multiline=false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  if (!isAdmin) return <Tag style={style}>{value}</Tag>;

  if (editing) {
    const save   = () => { setEditing(false); if (draft !== value) onSave(draft); };
    const cancel = () => { setEditing(false); setDraft(value); };
    const onKey  = e => {
      if (e.key === 'Escape') cancel();
      if (e.key === 'Enter' && !multiline) { e.preventDefault(); save(); }
    };
    const inputStyle = {
      width:'100%', padding:'4px 8px',
      border:`2px solid ${ACCENT}`, borderRadius:'5px',
      fontSize:'inherit', fontFamily:'inherit', lineHeight:'inherit',
      color:'inherit', background:'rgba(0,102,204,0.05)',
      outline:'none', resize: multiline ? 'vertical' : 'none', ...style,
    };
    return (
      <div>
        {multiline
          ? <textarea ref={inputRef} value={draft} onChange={e=>setDraft(e.target.value)}
              onKeyDown={onKey} rows={4} style={inputStyle}/>
          : <input ref={inputRef} value={draft} onChange={e=>setDraft(e.target.value)}
              onKeyDown={onKey} style={inputStyle}/>
        }
        <div style={{ display:'flex', gap:'4px', marginTop:'4px' }}>
          <SmallBtn onClick={save}>✓ Desar</SmallBtn>
          <SmallBtn onClick={cancel} bg="#888">✕</SmallBtn>
        </div>
      </div>
    );
  }

  return (
    <Tag onClick={() => setEditing(true)} title="Clic per editar" style={{
      ...style, cursor:'text', borderRadius:'3px',
      outline:'2px dashed transparent', transition:'outline 0.15s, background 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.outline=`2px dashed ${ACCENT}`; e.currentTarget.style.background='rgba(0,102,204,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.outline='2px dashed transparent'; e.currentTarget.style.background='transparent'; }}
    >{value}</Tag>
  );
}

/* ── EditableImage ── */
export function EditableImage({ src, onSave, isAdmin, imgStyle, alt='' }) {
  const [showPanel, setShowPanel] = useState(false);
  const [urlDraft, setUrlDraft]   = useState('');
  const fileRef = useRef(null);

  const handleFile = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { onSave(ev.target.result); setShowPanel(false); };
    reader.readAsDataURL(file);
  };

  const handleUrl = () => {
    if (urlDraft.trim()) { onSave(urlDraft.trim()); setShowPanel(false); setUrlDraft(''); }
  };

  if (!isAdmin) return <img src={src} alt={alt} style={imgStyle}/>;

  return (
    <div style={{ position:'relative', width:'100%' }}>
      <img src={src} alt={alt} style={{ ...imgStyle, cursor:'pointer' }}
        onClick={() => setShowPanel(p=>!p)}/>
      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
        justifyContent:'center', background:'rgba(0,0,0,0.45)',
        borderRadius:imgStyle?.borderRadius||'4px',
        opacity: showPanel ? 1 : 0, transition:'opacity 0.2s', pointerEvents:'none',
        color:'#fff', fontSize:'0.85rem', fontWeight:600 }}>
        📷 Canviar imatge
      </div>
      {showPanel && (
        <div style={{
          position:'absolute', top:'calc(100% + 8px)', left:0, zIndex:200,
          background:'#fff', border:`2px solid ${ACCENT}`, borderRadius:'8px',
          padding:'1rem', width:'300px', boxShadow:'0 8px 32px rgba(0,0,0,0.2)',
        }}>
          <p style={{ fontSize:'0.8rem', fontWeight:700, color:ACCENT, marginBottom:'0.5rem' }}>Canviar imatge</p>
          <p style={{ fontSize:'0.75rem', color:'#666', marginBottom:'4px' }}>Pujar fitxer:</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
            style={{ fontSize:'0.75rem', width:'100%', marginBottom:'0.75rem' }}/>
          <p style={{ fontSize:'0.75rem', color:'#666', marginBottom:'4px' }}>O enganxa URL:</p>
          <input type="text" placeholder="https://..." value={urlDraft}
            onChange={e=>setUrlDraft(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleUrl()}
            style={{ width:'100%', padding:'5px 8px', border:'1px solid #ccc',
              borderRadius:'5px', fontSize:'0.75rem', marginBottom:'6px', fontFamily:'inherit' }}/>
          <div style={{ display:'flex', gap:'6px' }}>
            <SmallBtn onClick={handleUrl}>Desar URL</SmallBtn>
            <SmallBtn onClick={()=>setShowPanel(false)} bg="#888">Cancel</SmallBtn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── EditableSliderManager ── */
export function EditableSliderManager({ fotos, onSave, isAdmin }) {
  const [open, setOpen]         = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const fileRef = useRef(null);

  if (!isAdmin) return null;

  const handleFile = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { onSave([...fotos, ev.target.result]); if(fileRef.current) fileRef.current.value=''; };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ marginTop:'1.25rem' }}>
      <button onClick={()=>setOpen(p=>!p)} style={{
        display:'flex', alignItems:'center', gap:6,
        background:'#fff', color:'#003366',
        border:'1.5px solid #dbe7fb', borderRadius:'8px',
        padding:'0.45rem 1rem', fontSize:'0.8rem', fontWeight:700,
        cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 8px rgba(0,51,102,0.06)',
      }}><Upload size={13}/> Gestionar fotos ({fotos.length})</button>

      {open && (
        <div style={{ marginTop:'0.75rem', background:'#fff',
          border:'1px solid #eef2ff', borderRadius:'12px', padding:'1rem',
          boxShadow:'0 8px 24px rgba(0,51,102,0.1)' }}>
          <div style={{ maxHeight:160, overflowY:'auto', display:'flex',
            flexDirection:'column', gap:'5px', marginBottom:'0.75rem' }}>
            {fotos.map((foto,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px',
                background:'#f8f9fe', padding:'6px', borderRadius:'6px' }}>
                <img src={foto.startsWith('data:')||foto.startsWith('http') ? foto : `assets/que-fem/${foto}`}
                  alt="" style={{ width:44, height:33, objectFit:'cover', borderRadius:'4px' }}/>
                <span style={{ flex:1, fontSize:'0.72rem', color:'#4a4a6a',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {foto.startsWith('data:') ? `[Imatge ${i+1}]` : foto}
                </span>
                <button onClick={()=>onSave(fotos.filter((_,j)=>j!==i))}
                  style={{ background:'#fde8ea', color:'#dc3545', border:'none',
                    borderRadius:'4px', padding:'3px 8px', cursor:'pointer', display:'flex',
                    fontSize:'0.72rem', fontFamily:'inherit' }}><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
          <p style={{ fontSize:'0.75rem', color:'#8888aa', marginBottom:'4px',
            display:'flex', alignItems:'center', gap:5 }}><Upload size={12}/> Afegir fitxer:</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
            style={{ fontSize:'0.75rem', width:'100%', marginBottom:'0.75rem', color:'#4a4a6a' }}/>
          <p style={{ fontSize:'0.75rem', color:'#8888aa', marginBottom:'4px',
            display:'flex', alignItems:'center', gap:5 }}><Link2 size={12}/> Afegir URL:</p>
          <div style={{ display:'flex', gap:'6px' }}>
            <input type="text" placeholder="https://..." value={urlDraft}
              onChange={e=>setUrlDraft(e.target.value)}
              style={{ flex:1, padding:'6px 9px', background:'#f8f9fe',
                border:'1px solid #dbe7fb', borderRadius:'6px',
                fontSize:'0.75rem', color:'#1a1a2e', fontFamily:'inherit' }}/>
            <SmallBtn onClick={()=>{ if(urlDraft.trim()){ onSave([...fotos,urlDraft.trim()]); setUrlDraft(''); } }}>
              <Check size={12}/>
            </SmallBtn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── EditableMosaicPhoto ──
   Una foto individual del mosaic de la Intro. Si `src` és null i és admin,
   mostra una targeta buida amb "+" per afegir-hi una foto (fitxer o URL).
   Si `src` existeix i és admin, permet substituir-la o eliminar-la. */
export function EditableMosaicPhoto({ src, onReplace, onRemove, isAdmin, style }) {
  const [showPanel, setShowPanel] = useState(false);
  const [urlDraft, setUrlDraft]   = useState('');
  const fileRef = useRef(null);

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { onReplace(ev.target.result); setShowPanel(false); };
    reader.readAsDataURL(file);
  };
  const handleUrl = () => {
    if (urlDraft.trim()) { onReplace(urlDraft.trim()); setShowPanel(false); setUrlDraft(''); }
  };

  if (!isAdmin) {
    if (!src) return null;
    return <img src={src} alt="" style={style} />;
  }

  return (
    <div style={{ position:'relative', flexShrink:0 }}>
      {src ? (
        <img src={src} alt="" style={{ ...style, cursor:'pointer' }}
          onClick={() => setShowPanel(p => !p)} />
      ) : (
        <button onClick={() => setShowPanel(p => !p)} style={{
          ...style, border:'2px dashed rgba(0,51,102,0.25)', background:'rgba(0,51,102,0.03)',
          display:'flex', alignItems:'center', justifyContent:'center',
          color:'rgba(0,51,102,0.4)', cursor:'pointer',
        }}><ImagePlus size={22}/></button>
      )}

      {showPanel && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)',
          zIndex:300, background:'#0f1e35', border:'1px solid rgba(255,215,0,0.25)',
          borderRadius:'10px', padding:'0.85rem', width:220,
          boxShadow:'0 12px 30px rgba(0,0,0,0.35)',
        }}>
          <p style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', marginBottom:'4px',
            display:'flex', alignItems:'center', gap:5 }}><Upload size={12}/> Pujar fitxer</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
            style={{ fontSize:'0.72rem', width:'100%', marginBottom:'0.6rem', color:'rgba(255,255,255,0.6)' }}/>
          <p style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', marginBottom:'4px',
            display:'flex', alignItems:'center', gap:5 }}><Link2 size={12}/> O enganxa URL</p>
          <div style={{ display:'flex', gap:6, marginBottom: src ? '0.6rem' : 0 }}>
            <input type="text" placeholder="https://..." value={urlDraft}
              onChange={e => setUrlDraft(e.target.value)}
              onKeyDown={e => e.key==='Enter' && handleUrl()}
              style={{ flex:1, padding:'5px 8px', background:'rgba(255,255,255,0.08)',
                border:'1px solid rgba(255,255,255,0.15)', borderRadius:'4px',
                fontSize:'0.72rem', color:'#fff', fontFamily:'inherit' }}/>
            <SmallBtn onClick={handleUrl}><Check size={12}/></SmallBtn>
          </div>
          {src && (
            <button onClick={() => { onRemove(); setShowPanel(false); }} style={{
              display:'flex', alignItems:'center', gap:5, width:'100%',
              justifyContent:'center', background:'rgba(220,53,69,0.15)', color:'#ff8a94',
              border:'none', borderRadius:'5px', padding:'5px', cursor:'pointer',
              fontSize:'0.72rem', fontWeight:600, fontFamily:'inherit',
            }}><Trash2 size={12}/> Eliminar foto</button>
          )}
          <button onClick={() => setShowPanel(false)} style={{
            position:'absolute', top:6, right:6, background:'none', border:'none',
            color:'rgba(255,255,255,0.4)', cursor:'pointer', display:'flex', padding:2,
          }}><X size={13}/></button>
        </div>
      )}
    </div>
  );
}